import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getTask,
  hangupTask,
  openLiveSocket,
  type LiveEvent,
  type Task,
  type TaskEvent,
} from '../api';
import { StatusPill } from '../components/StatusPill';
import { Transcript, type TranscriptTurn } from '../components/Transcript';

function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export function TaskView() {
  const { id } = useParams<{ id: string }>();
  const [task, setTask] = useState<Task | null>(null);
  const [turns, setTurns] = useState<TranscriptTurn[]>([]);
  const [phase, setPhaseState] = useState<PhaseInfo | null>(null);
  const [now, setNow] = useState(Date.now());
  const [hangingUp, setHangingUp] = useState(false);
  const turnsRef = useRef<TranscriptTurn[]>([]);

  async function onHangup() {
    if (!id || hangingUp) return;
    setHangingUp(true);
    try {
      await hangupTask(id);
    } catch {
      // ignore — UI will reflect state on next poll
    } finally {
      setHangingUp(false);
    }
  }

  // Tick clock for live elapsed display
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  // Initial load + repoll task status while live
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { task, events } = await getTask(id);
        if (cancelled) return;
        setTask(task);
        const seeded = buildTurns(events);
        turnsRef.current = seeded;
        setTurns([...seeded]);
        const lastPhase = lastPhaseFromEvents(events);
        if (lastPhase) setPhaseState(lastPhase);
      } catch {
        // ignore
      }
    };
    load();
    const poll = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [id]);

  // Live WS for streaming events (in addition to polling task state)
  useEffect(() => {
    if (!id) return;
    const ws = openLiveSocket(id, (evt) => {
      if (evt.kind === 'phase') {
        setPhaseState(evt.payload as PhaseInfo);
        return;
      }
      applyLiveEvent(evt, turnsRef, setTurns);
    });
    return () => ws.close();
  }, [id]);

  if (!id) return null;
  if (!task) {
    return (
      <div className="main-inner">
        <div className="empty-state">
          <div className="empty-state-title">Loading…</div>
        </div>
      </div>
    );
  }

  const isLive = task.status === 'dialing' || task.status === 'in_call';
  const startedAt = task.started_at ?? task.created_at;
  const endedAt = task.ended_at ?? (isLive ? now : task.created_at);

  return (
    <div className="main-inner">
      {(isLive || phase) && (
        <div className="live-bar">
          <span
            className="live-dot"
            style={{ background: phaseColor(phase?.phase), boxShadow: `0 0 0 4px ${phaseColor(phase?.phase)}22` }}
          />
          <span className="live-label" style={{ color: phaseColor(phase?.phase) }}>
            {phaseLabel(phase)}
          </span>
          {isLive && <span className="live-elapsed">{fmtElapsed(endedAt - startedAt)}</span>}
          {isLive && (
            <button
              className="hangup-btn"
              onClick={onHangup}
              disabled={hangingUp}
              title="End the call now"
            >
              {hangingUp ? 'Hanging up…' : '■ Hang up'}
            </button>
          )}
        </div>
      )}

      <header className="call-header">
        <div className="call-meta">
          <div className="eyebrow">Call</div>
          <div className="call-number">{task.phone_number}</div>
          <div className="call-instruction">{task.instruction}</div>
        </div>
        <StatusPill status={task.status} />
      </header>

      {!isLive && (
        <div
          className={`detail-summary ${
            task.success === 1 ? 'success' : task.success === 0 ? 'failure' : ''
          }`}
        >
          <div className="detail-summary-eyebrow">
            Result —{' '}
            {task.success === 1 ? 'Success' : task.success === 0 ? 'Did not succeed' : 'Pending'}
          </div>
          <div className="detail-summary-text">
            {task.summary ?? 'Generating summary…'}
          </div>
        </div>
      )}

      {task.extracted_data && (
        <ExtractedData json={task.extracted_data} />
      )}

      <section className="detail-section">
        <h2 className="detail-section-title">Transcript</h2>
        <Transcript turns={turns} autoScroll={isLive} />
      </section>

      <section className="detail-section">
        <h2 className="detail-section-title">Metadata</h2>
        <dl className="kv-grid">
          <dt>Task ID</dt>
          <dd className="mono">{task.id}</dd>
          {task.twilio_call_sid && (
            <>
              <dt>Call SID</dt>
              <dd className="mono">{task.twilio_call_sid}</dd>
            </>
          )}
          <dt>Created</dt>
          <dd className="mono">{new Date(task.created_at).toLocaleString()}</dd>
          {task.started_at && (
            <>
              <dt>Started</dt>
              <dd className="mono">{new Date(task.started_at).toLocaleString()}</dd>
            </>
          )}
          {task.ended_at && (
            <>
              <dt>Ended</dt>
              <dd className="mono">{new Date(task.ended_at).toLocaleString()}</dd>
            </>
          )}
          {task.error && (
            <>
              <dt>Error</dt>
              <dd className="mono">{task.error}</dd>
            </>
          )}
        </dl>
      </section>
    </div>
  );
}

function ExtractedData({ json }: { json: string }) {
  let data: Record<string, unknown> = {};
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  const entries = Object.entries(data);
  if (entries.length === 0) return null;
  return (
    <section className="detail-section">
      <h2 className="detail-section-title">Extracted</h2>
      <dl className="kv-grid">
        {entries.map(([k, v]) => (
          <Pair key={k} k={k} v={v} />
        ))}
      </dl>
    </section>
  );
}

function Pair({ k, v }: { k: string; v: unknown }) {
  const display = typeof v === 'string' ? v : JSON.stringify(v);
  return (
    <>
      <dt>{k.replace(/_/g, ' ')}</dt>
      <dd>{display}</dd>
    </>
  );
}

// ───────────────── Live call phase ─────────────────

interface PhaseInfo {
  phase: string;
  by?: string;
  cause?: string;
  reason?: string;
}

function lastPhaseFromEvents(events: TaskEvent[]): PhaseInfo | null {
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i]!.kind === 'phase') {
      try {
        return JSON.parse(events[i]!.payload) as PhaseInfo;
      } catch {
        return null;
      }
    }
  }
  return null;
}

function phaseLabel(p: PhaseInfo | null): string {
  if (!p) return 'Live';
  switch (p.phase) {
    case 'placed':
      return 'Placing call…';
    case 'ringing':
      return 'Ringing…';
    case 'connected':
      return 'Connected';
    case 'streaming':
      return 'Agent connecting…';
    case 'ai_speaking':
      return 'Agent speaking';
    case 'listening':
      return 'Caller speaking';
    case 'wrapping_up':
      return 'Wrapping up…';
    case 'ended':
      if (p.by === 'caller_hung_up') return 'Caller hung up';
      if (p.by === 'agent_ended') return 'Agent ended the call';
      return 'Call ended';
    case 'failed':
      return `Failed${p.reason ? ` — ${p.reason}` : ''}`;
    default:
      return p.phase;
  }
}

function phaseColor(phase?: string): string {
  switch (phase) {
    case 'ai_speaking':
      return '#c7522a';
    case 'listening':
      return '#5c6b3f';
    case 'connected':
    case 'streaming':
      return '#5c6b3f';
    case 'ended':
      return '#8b847a';
    case 'failed':
      return '#8e2d22';
    default:
      return '#c7522a';
  }
}

// Reconstruct turns from persisted events. Prefer finals, but if a turn only
// ever produced partials, keep the longest partial so the transcript still
// shows. Mirrors the backend's buildTranscript logic.
function buildTurns(events: TaskEvent[]): TranscriptTurn[] {
  const out: TranscriptTurn[] = [];
  let role: TranscriptTurn['role'] | null = null;
  let bestText = '';
  let sawFinal = false;
  let ts = 0;

  const flush = () => {
    if (role && bestText.trim()) {
      out.push({ role, text: bestText.trim(), isFinal: sawFinal, ts });
    }
    role = null;
    bestText = '';
    sawFinal = false;
  };

  for (const ev of events) {
    if (ev.kind !== 'transcript_user' && ev.kind !== 'transcript_ai') continue;
    let p: { text?: string; isFinal?: boolean };
    try {
      p = JSON.parse(ev.payload);
    } catch {
      continue;
    }
    if (!p.text) continue;
    const r: TranscriptTurn['role'] = ev.kind === 'transcript_ai' ? 'ai' : 'callee';
    if (r !== role) {
      flush();
      role = r;
    }
    if (p.text.length > bestText.length) bestText = p.text;
    if (p.isFinal) sawFinal = true;
    ts = ev.ts;
  }
  flush();
  return out;
}

function applyLiveEvent(
  evt: LiveEvent,
  turnsRef: React.MutableRefObject<TranscriptTurn[]>,
  setTurns: (t: TranscriptTurn[]) => void,
): void {
  if (evt.kind !== 'transcript_user' && evt.kind !== 'transcript_ai') return;
  const p = evt.payload as { text?: string; isFinal?: boolean };
  if (!p?.text) return;
  const role: TranscriptTurn['role'] = evt.kind === 'transcript_ai' ? 'ai' : 'callee';
  const next = [...turnsRef.current];
  upsertPartial(next, { role, text: p.text, isFinal: !!p.isFinal, ts: evt.ts });
  turnsRef.current = next;
  setTurns(next);
}

// If the last turn is the same role and not final, replace it; else append.
function upsertPartial(turns: TranscriptTurn[], turn: TranscriptTurn): void {
  const last = turns[turns.length - 1];
  if (last && last.role === turn.role && !last.isFinal) {
    turns[turns.length - 1] = turn;
  } else {
    turns.push(turn);
  }
}

